import { ReactNode } from 'react';
import { CoachAnalysis, GRADE_META, SAMPLE_RULE_TEXT, compareMetric } from './coachAnalysis';
import { CoachRow, won } from './tabData';
import { RadarChart, buildRadar } from './CoachRadar';
import DeviationBar from './CoachDeviationBar';
import { quadrantOf, quadrantMeta, CARE_AXIS_LABEL } from './coachQuadrant';
import OpsDetailModal from './OpsDetailModal';

/**
 * 강사 데이터 — 강사 카드를 누르면 **먼저** 열리는 화면. 화면 한가운데 뜨는 팝업이고,
 * 껍데기는 회원 상세·브리핑 상세와 같은 `OpsDetailModal` 을 쓴다.
 * (예전에는 오른쪽 서랍이었다 — 왜 옮겼는지는 OpsDetailModal 첫머리에 있다.)
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 다시 만들었나, 그리고 지난번과 무엇이 다른가
 * ─────────────────────────────────────────────────────────────
 * 예전에도 강사 상세 패널(CoachDetailDrawer)이 있었고 **지웠습니다.**
 * 그 패널이 리포트(PDF)와 **다른 데이터를 먹었기 때문**입니다 — 같은 강사가
 * 패널에서는 '핵심 인재', 인쇄물에서는 '점검 필요' 였습니다. 원장님이 면담 자리에
 * 들고 가는 종이와 화면이 다른 말을 하면 화면 전체를 못 믿게 됩니다.
 *
 * 그래서 이 파일에는 **계산이 한 줄도 없습니다.** 전부 받아 씁니다.
 *   · 등급·강점·약점·편차  `coachAnalysis.analyzeAll` 이 만든 `CoachAnalysis` 그대로
 *                          (PDF 의 `composeReport` 도 같은 `analyzeCoach` 를 씁니다)
 *   · 레이더               `CoachRadar.buildRadar` — PDF 가 쓰는 그 함수
 *   · 지표 비교 막대        `CoachDeviationBar` — 강사 현황 「지표 펼치기」와 같은 조각
 *   · 유형(사분면)          `coachQuadrant.quadrantOf` — 강사 현황 산점도와 같은 판정
 *   · 순위                 `coachAnalysis.compareMetric` — 강사 현황·PDF 와 같은 규칙
 *
 * 새 숫자를 만들지 않는 것이 이 파일의 유일한 규칙입니다.
 * 여기에 계산을 한 줄이라도 넣으면 지난번 그대로 됩니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 평가 유보 강사
 * ─────────────────────────────────────────────────────────────
 * 표본이 모자란 강사(`grade === 'INSUFFICIENT'`)에게는 강점·약점·순위·유형을
 * 만들지 않습니다. 강사 현황과 **같은 규칙**입니다 — 배지는 '평가 유보'인데
 * 아래에 "▲ 잘하고 있는 것"이 붙으면 화면이 스스로 모순됩니다.
 * 대신 무엇이 쌓여야 평가가 시작되는지를 적습니다.
 */

const BORDER = '#E3EAF3';

type Props = {
    /** 이 강사의 분석 결과. 계산은 이미 끝나 있어야 한다 */
    analysis: CoachAnalysis;
    /**
     * 순위·비교의 모집단. 강사 현황·PDF 와 **같은 배열**을 넘겨야 한다.
     * 일부만 넘기면 "센터 3위"가 화면마다 달라진다.
     */
    peers: CoachRow[];
    /** 보고 있는 달 — 머리말에 적는다 */
    month: string;
    /** 사분면 가로 기준선(전환율 센터 평균). 없으면 유형을 만들지 않는다 */
    convMid: number | null;
    /** 사분면 세로 기준선(일지 작성률 센터 평균) */
    careMid: number | null;
    onClose: () => void;
    /** 하단 '월간 리포트(PDF)' 버튼 */
    onOpenReport: () => void;
};

/** 작은 제목 — 구역을 나누되 눈을 뺏지 않는다 */
const SectionTitle = ({ children, sub }: { children: ReactNode; sub?: string }) => (
    <div className="flex items-baseline" style={{ gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E' }}>{children}</span>
        {sub && <span style={{ fontSize: 12.5, color: '#93A0AE' }}>{sub}</span>}
    </div>
);

const Card = ({ children }: { children: ReactNode }) => (
    <div
        className="rounded-[12px]"
        style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '16px 18px' }}
    >
        {children}
    </div>
);

const CoachDataDrawer = ({
    analysis,
    peers,
    month,
    convMid,
    careMid,
    onClose,
    onOpenReport,
}: Props) => {
    const { coach, grade, sample, deviations, strengths, weaknesses, unmeasured } = analysis;
    const g = GRADE_META[grade];
    const held = grade === 'INSUFFICIENT';

    /*
     * 유형(사분면).
     * 유보 강사는 애초에 사분면에 놓이지 않는다(quadrantOf 안에서 checkSample 로 거른다).
     * 그래도 여기서 한 번 더 막지 않는 이유는, 한 곳에서만 판정해야 두 화면이 안 갈리기 때문이다.
     */
    const quad = quadrantMeta(quadrantOf(coach, convMid, careMid));

    /* 레이더 — PDF 가 쓰는 그 함수 그대로 */
    const radar = buildRadar(coach);

    return (
        <OpsDetailModal
            label={`${coach.name} 강사 데이터`}
            closeLabel="강사 데이터 닫기"
            width={760}
            onClose={onClose}
            header={
                <div className="flex items-center" style={{ gap: 14 }}>
                    <div
                        className="flex items-center justify-center rounded-full"
                        style={{
                            width: 48,
                            height: 48,
                            background: 'rgba(255,255,255,0.16)',
                            color: '#FFFFFF',
                            fontSize: 19,
                            fontWeight: 800,
                            flex: 'none',
                        }}
                    >
                        {coach.name.slice(0, 1)}
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center" style={{ gap: 8 }}>
                            <span
                                style={{
                                    fontSize: 20,
                                    fontWeight: 800,
                                    color: '#FFFFFF',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {coach.name} 강사
                            </span>
                            <span
                                className="rounded-[5px]"
                                title={g.desc}
                                style={{
                                    background: 'rgba(255,255,255,0.16)',
                                    color: '#FFFFFF',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                    cursor: 'help',
                                }}
                            >
                                {g.label}
                            </span>
                            {/*
                              유형은 등급과 다른 축이다 — 유보 강사에게는 아예 없다.

                              ⚠️ 두 라벨이 같은 글자일 수 있다 (등급 CRITICAL 과
                                 사분면 RISK 가 둘 다 '집중 관리'). 그대로 두면 같은
                                 배지가 나란히 두 개 붙어 화면이 고장 난 것처럼 보인다.
                                 같으면 하나만 남긴다.
                            */}
                            {quad && quad.label !== g.label && (
                                <span
                                    className="rounded-[5px]"
                                    title={quad.howTo}
                                    style={{
                                        background: 'rgba(255,255,255,0.10)',
                                        color: '#CBDCF5',
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        padding: '3px 8px',
                                        cursor: 'help',
                                    }}
                                >
                                    {quad.label}
                                </span>
                            )}
                        </div>
                        <span style={{ fontSize: 13, color: '#9DB4D8', marginTop: 4 }}>
                            {month} · 담당 {coach.members}명 (활성 {coach.activeMembers}명) · 수업{' '}
                            {coach.lessons}회
                        </span>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col" style={{ gap: 12 }}>
                {/* 챙길 거리 배지 — 지표와 무관하게 지금 손이 필요한 것들 */}
                {((coach.pendingRequests ?? 0) > 0 || (coach.appUnlinked ?? 0) > 0) && (
                    <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {(coach.pendingRequests ?? 0) > 0 && (
                            <span
                                className="rounded-[6px]"
                                title="회원이 보낸 예약 요청 중 아직 강사가 응답하지 않은 건수입니다."
                                style={{
                                    background: '#FFF0F0',
                                    color: '#C22D38',
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    padding: '5px 10px',
                                    cursor: 'help',
                                }}
                            >
                                미응답 예약 {coach.pendingRequests}건
                            </span>
                        )}
                        {(coach.appUnlinked ?? 0) > 0 && (
                            <span
                                className="rounded-[6px]"
                                title="담당 회원 중 라포 앱을 연결하지 않은 인원입니다. 일지·알림이 닿지 않습니다."
                                style={{
                                    /* 4명 이상이면 노란색 — 강사 현황 목록과 같은 기준 */
                                    background:
                                        (coach.appUnlinked ?? 0) >= 4 ? '#FEF3C7' : '#F1F4F9',
                                    color: (coach.appUnlinked ?? 0) >= 4 ? '#B45309' : '#7C8794',
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    padding: '5px 10px',
                                    cursor: 'help',
                                }}
                            >
                                앱 미연동 {coach.appUnlinked}명
                            </span>
                        )}
                    </div>
                )}

                {/*
                  평가 유보.
                  강사 현황과 **같은 문장·같은 규칙**이다. 여기만 다르게 적으면
                  같은 강사가 화면마다 다른 이유로 유보된 것처럼 보인다.
                */}
                {held && (
                    <Card>
                        <span style={{ fontSize: 14, color: '#5B6B7F', lineHeight: '21px' }}>
                            표본이 모자라 강점·개선점을 가르지 않았습니다 ({sample.reason}).{' '}
                            {SAMPLE_RULE_TEXT}.
                        </span>
                    </Card>
                )}

                {/* 레이더 + 점수 */}
                <Card>
                    <SectionTitle sub="못 잰 축은 그리지 않습니다 — 0 으로 찍으면 최악으로 읽힙니다">
                        지표 한눈에
                    </SectionTitle>
                    <div className="flex items-center" style={{ gap: 22 }}>
                        <div style={{ flex: 'none' }}>
                            <RadarChart values={radar} size={220} showScores />
                        </div>
                        <div className="flex flex-1 flex-col" style={{ gap: 7 }}>
                            {radar.map((r) => (
                                <div
                                    key={r.label}
                                    className="flex items-center"
                                    style={{ gap: 10 }}
                                >
                                    <span
                                        style={{
                                            fontSize: 13,
                                            color: '#5B6B7F',
                                            width: 56,
                                            flex: 'none',
                                        }}
                                    >
                                        {r.label}
                                    </span>
                                    <div
                                        style={{
                                            flex: 1,
                                            height: 8,
                                            borderRadius: 4,
                                            background: '#EEF2F8',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {r.score != null && (
                                            <div
                                                style={{
                                                    width: `${Math.max(Math.min(r.score, 100), 0)}%`,
                                                    height: '100%',
                                                    background: '#3D73DD',
                                                }}
                                            />
                                        )}
                                    </div>
                                    <span
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: r.score == null ? '#A7B0BC' : '#16202E',
                                            width: 52,
                                            flex: 'none',
                                            textAlign: 'right',
                                        }}
                                    >
                                        {/* 못 잰 축은 '—'. 0 점이 아니다 */}
                                        {r.score == null ? '못 쟀음' : Math.round(r.score)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>

                {/* 지표 비교 — 센터 평균 대비 */}
                <Card>
                    <SectionTitle sub="가운데 선이 센터 평균입니다">
                        센터 평균 대비
                    </SectionTitle>
                    {deviations.length === 0 ? (
                        <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                            비교할 지표가 아직 없습니다. 센터 평균이 잡히면 자동으로
                            나타납니다.
                        </span>
                    ) : (
                        <div className="flex flex-col" style={{ gap: 8 }}>
                            {deviations.map((d) => (
                                <DeviationBar key={d.metric.key} d={d} />
                            ))}
                        </div>
                    )}
                    {unmeasured.length > 0 && (
                        <span
                            style={{
                                display: 'block',
                                marginTop: 11,
                                fontSize: 12,
                                color: '#93A0AE',
                            }}
                        >
                            아직 못 잰 지표: {unmeasured.map((m) => m.label).join(' · ')}
                        </span>
                    )}
                </Card>

                {/*
                  순위.
                  ⚠️ 유보 강사에게는 만들지 않는다 — 표본 1명으로 매긴 '센터 1위'는
                     표본 1명으로 매긴 '꼴찌'와 똑같이 틀렸다.
                */}
                {!held && (
                    <Card>
                        <SectionTitle sub="표본이 모자란 동료는 모집단에서 뺐습니다">
                            센터 안에서의 자리
                        </SectionTitle>
                        <div className="flex flex-col" style={{ gap: 7 }}>
                            {deviations.map((d) => {
                                const cmp = compareMetric(coach, d.metric, peers);
                                if (!cmp || cmp.rank == null || cmp.rankOf == null) return null;
                                return (
                                    <div
                                        key={d.metric.key}
                                        className="flex items-center"
                                        style={{ gap: 10 }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 13,
                                                color: '#5B6B7F',
                                                width: 118,
                                                flex: 'none',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {d.metric.label}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 700,
                                                color: '#16202E',
                                            }}
                                        >
                                            {cmp.rankOf}명 중 {cmp.rank}위
                                        </span>
                                        <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                                            내 {cmp.mine}
                                            {d.metric.unit} · 평균 {cmp.avg}
                                            {d.metric.unit}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                )}

                {/*
                  강점 / 보완.
                  유보 강사에게는 그리지 않는다 (강사 현황과 같은 규칙).
                */}
                {!held && (strengths.length > 0 || weaknesses.length > 0) && (
                    <div className="flex" style={{ gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <Card>
                                <SectionTitle>▲ 잘하고 있는 것</SectionTitle>
                                {strengths.length === 0 ? (
                                    <span style={{ fontSize: 13, color: '#93A0AE' }}>
                                        아직 평균을 뚜렷하게 웃도는 지표가 없습니다
                                    </span>
                                ) : (
                                    <div className="flex flex-col" style={{ gap: 6 }}>
                                        {strengths.map((d) => (
                                            <span
                                                key={d.metric.key}
                                                style={{ fontSize: 13.5, color: '#00795F' }}
                                            >
                                                {d.metric.label} {d.value}
                                                {d.metric.unit}{' '}
                                                <span style={{ color: '#A7B0BC' }}>
                                                    (평균 {d.avg}
                                                    {d.metric.unit})
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Card>
                                <SectionTitle>▼ 보완할 것</SectionTitle>
                                {weaknesses.length === 0 ? (
                                    <span style={{ fontSize: 13, color: '#93A0AE' }}>
                                        평균을 뚜렷하게 밑도는 지표가 없습니다
                                    </span>
                                ) : (
                                    <div className="flex flex-col" style={{ gap: 6 }}>
                                        {weaknesses.map((d) => (
                                            <span
                                                key={d.metric.key}
                                                style={{ fontSize: 13.5, color: '#C22D38' }}
                                            >
                                                {d.metric.label} {d.value}
                                                {d.metric.unit}{' '}
                                                <span style={{ color: '#A7B0BC' }}>
                                                    (평균 {d.avg}
                                                    {d.metric.unit})
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </div>
                    </div>
                )}

                {/* 유형 안내 — 무엇을 하면 되는지 */}
                {quad && (
                    <Card>
                        <SectionTitle sub={`${quad.desc} · 세로축은 ${CARE_AXIS_LABEL}`}>
                            {quad.label} 유형
                        </SectionTitle>
                        <span style={{ fontSize: 13.5, color: '#425063', lineHeight: '21px' }}>
                            {quad.howTo}
                        </span>
                    </Card>
                )}

                <Card>
                    <div className="flex items-center" style={{ gap: 12 }}>
                        <span style={{ fontSize: 13.5, color: '#5B6B7F' }}>
                            미사용 선수금{' '}
                            <b style={{ color: '#16202E' }}>{won(coach.portfolioValue)}원</b>
                        </span>
                        <button
                            type="button"
                            onClick={onOpenReport}
                            className="rounded-[9px]"
                            style={{
                                marginLeft: 'auto',
                                height: 40,
                                padding: '0 18px',
                                background: '#1A305D',
                                border: 'none',
                                color: '#FFFFFF',
                                fontSize: 14.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                                flex: 'none',
                            }}
                        >
                            📄 월간 리포트(PDF)
                        </button>
                    </div>
                </Card>
            </div>
        </OpsDetailModal>
    );
};

export default CoachDataDrawer;
